import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";
import { useRecordContext } from "react-admin";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Grid,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";

const formatNumber = (value) =>
  typeof value === "number"
    ? `$${value.toLocaleString("en-US")}`
    : value ?? "-";

const formatPercent = (value) =>
  typeof value === "number" ? `${value}%` : value ?? "-";

const entrypoint = process.env.REACT_APP_API_PLATFORM_ENDPOINT;

const ALLOWED_ROLES = ["ROLE_ADMIN"];

const MLPanelMessage = ({ children }) => (
  <Grid id="mlPricingPanel" sx={{ gridColumn: "1 / span 6" }}>
    <Card>
      <CardContent>{children}</CardContent>
    </Card>
  </Grid>
);

MLPanelMessage.propTypes = {
  children: PropTypes.node,
};

const MLPricingPanel = () => {
  const roles = localStorage.getItem("permissions") ?? "";
  const showMLPricingPanel = ALLOWED_ROLES.some((role) => roles.includes(role));

  const record = useRecordContext();
  const [pricingData, setPricingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPricingData = useCallback(async () => {
    if (!record?.originId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${entrypoint}/services/pricing/get-historical-pricing?leadId=${record.originId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const responseJSON = await response.json();

      if (responseJSON.Error) {
        throw new Error(responseJSON.Error);
      }

      if (responseJSON.result === "error") {
        throw new Error(responseJSON.error_msg || "Pricing fetch error");
      }

      const data = Array.isArray(responseJSON)
        ? responseJSON
        : Array.isArray(responseJSON.data)
        ? responseJSON.data
        : [responseJSON];

      setPricingData(data);
    } catch (err) {
      setPricingData([]);
      setError(err.message || "Failed to fetch pricing data");
    } finally {
      setIsLoading(false);
    }
  }, [entrypoint, record?.originId]);

  useEffect(() => {
    if (!record?.originId) return;

    fetchPricingData();

    window.addEventListener("fetchPricingData", fetchPricingData);

    return () => {
      window.removeEventListener("fetchPricingData", fetchPricingData);
    };
  }, [fetchPricingData]);

  if (!showMLPricingPanel) return <></>;

  if (isLoading)
    return (
      <MLPanelMessage>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      </MLPanelMessage>
    );

  if (error)
    return (
      <MLPanelMessage>
        <Box sx={{ color: "red", textAlign: "center", my: 2 }}>{error}</Box>
      </MLPanelMessage>
    );

  if (!pricingData.length)
    return (
      <MLPanelMessage>
        <Box sx={{ textAlign: "center", my: 2 }}>
          No pricing data available.
        </Box>
      </MLPanelMessage>
    );

  const latest = pricingData[0] || {};

  return (
    <>
      <Grid id="mlPricingPanel" sx={{ gridColumn: "1 / span 6" }}>
        <Card>
          <CardContent>
            <CardHeader
              title="Pricing Data"
              sx={{ padding: "10px" }}
              titleTypographyProps={{
                sx: {
                  marginBottom: "5px",
                  textTransform: "uppercase",
                  fontSize: "14px",
                  fontWeight: "bold",
                },
              }}
            />
            <Grid
              container
              direction="row"
              alignItems="center"
              columns={12}
              rowSpacing={1}
              columnSpacing={2}
            >
              <Grid item xs={2}>
                <TextField
                  label="Certainty Rating"
                  value={formatPercent(latest.certainty_rating)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Target Price"
                  value={formatNumber(latest.target_price)}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Guide Price"
                  value={formatNumber(latest.guide_price)}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Max Price"
                  value={formatNumber(latest.max_price)}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Predicted Auction Price (after fees)"
                  value={formatNumber(latest.auction_price_minus_fees)}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Predicted Auction Price"
                  value={formatNumber(latest.auction_price)}
                  disabled
                />
              </Grid>
            </Grid>
            <CardHeader
              title="Pricing History"
              sx={{ padding: "10px" }}
              titleTypographyProps={{
                sx: {
                  marginBottom: "-15px",
                  textTransform: "uppercase",
                  fontSize: "14px",
                  fontWeight: "bold",
                },
              }}
            />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Certainty Rating %</TableCell>
                    <TableCell>Target Price</TableCell>
                    <TableCell>Guide Price</TableCell>
                    <TableCell>Max Price</TableCell>
                    <TableCell>Predicted Auction Price (after fees)</TableCell>
                    <TableCell>Predicted Auction Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pricingData.map((row, idx) => (
                    <TableRow
                      key={`${row.id || row.date_created || idx}`}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell>{row.date_created}</TableCell>
                      <TableCell>
                        {formatPercent(row.certainty_rating)}
                      </TableCell>
                      <TableCell>{formatNumber(row.target_price)}</TableCell>
                      <TableCell>{formatNumber(row.guide_price)}</TableCell>
                      <TableCell>{formatNumber(row.max_price)}</TableCell>
                      <TableCell>
                        {formatNumber(row.auction_price_minus_fees)}
                      </TableCell>
                      <TableCell>{formatNumber(row.auction_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

export default MLPricingPanel;
